import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const NM = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea" }) : "");

const CAT_LABELS = {
  maintenance: "Maintenance", reparation: "Réparation", pneus: "Pneus", assurance: "Assurance",
  geolocalisation: "Géolocalisation", carburant: "Carburant", controle: "Contrôle technique", autre: "Autre",
};
const catLabel = (c) => CAT_LABELS[c] || c || "—";
const money = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} XPF`;

const COLS = [
  ["Date GPS", (p) => NM(p.timestamp)],
  ["Latitude", (p) => p.lat ?? ""],
  ["Longitude", (p) => p.lon ?? ""],
  ["Vitesse (km/h)", (p) => p.speed ?? ""],
  ["Direction", (p) => p.angle ?? ""],
  ["Altitude", (p) => p.altitude ?? ""],
  ["Satellites", (p) => p.satellites ?? ""],
  ["GSM", (p) => p.gsm_signal ?? ""],
  ["Contact", (p) => (p.ignition ? "ON" : "OFF")],
  ["Alim (mV)", (p) => p.external_voltage ?? ""],
];

const dl = (blob, name) => {
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = name; a.click();
  URL.revokeObjectURL(u);
};

// Export the latest records (max 500) of a unit as html / csv / xls / pdf (client-side).
export function exportUnitRecent(unit, rows, fmt) {
  const pts = (rows || []).slice(0, 500);
  const base = `${(unit.plate || unit.name || "unite").replace(/\s/g, "")}_donnees_recentes`;
  const headers = COLS.map((c) => c[0]);
  const matrix = pts.map((p) => COLS.map((c) => c[1](p)));

  if (fmt === "csv") {
    const csv = [`IMEI ${unit.imei || ""} - ${unit.name}`, headers.join(",")]
      .concat(matrix.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))).join("\n");
    dl(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }), base + ".csv");
  } else if (fmt === "xls") {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...matrix]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Donnees recentes");
    XLSX.writeFile(wb, base + ".xlsx");
  } else if (fmt === "html") {
    const rowsHtml = matrix.map((r) => `<tr>${r.map((v) => `<td>${v}</td>`).join("")}</tr>`).join("");
    const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Donnees recentes - ${unit.name}</title>
<style>body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#0f172a}h1{color:#17A2B8;font-size:20px;margin:0}.sub{color:#64748b;font-size:12px;margin:6px 0 16px}table{border-collapse:collapse;width:100%;font-size:12px}th{background:#17A2B8;color:#fff;text-align:left;padding:6px 8px}td{border-bottom:1px solid #e2e8f0;padding:5px 8px}tr:nth-child(even) td{background:#f8fafc}</style></head>
<body><h1>Boomerang Pacific Tracking &mdash; Donnees recentes</h1>
<div class="sub">${unit.name} &middot; IMEI ${unit.imei || "&mdash;"} &middot; ${pts.length} point(s) &middot; fuseau Pacific/Noumea</div>
<table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`;
    dl(new Blob([html], { type: "text/html;charset=utf-8;" }), base + ".html");
  } else if (fmt === "pdf") {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    doc.setFillColor(23, 162, 184); doc.rect(0, 0, 297, 16, "F");
    doc.setTextColor(255); doc.setFontSize(13);
    doc.text("Boomerang Pacific Tracking - Donnees recentes", 10, 10);
    doc.setTextColor(15, 23, 42); doc.setFontSize(9);
    doc.text(`${unit.name}  -  IMEI ${unit.imei || "-"}  -  ${pts.length} point(s)  -  Pacific/Noumea`, 10, 23);
    autoTable(doc, { head: [headers], body: matrix, startY: 27, styles: { fontSize: 6.5, cellPadding: 1 }, headStyles: { fillColor: [23, 162, 184] } });
    doc.save(base + ".pdf");
  }
}

// Cost report by family (category) + detailed invoices, over a period. Formats: csv / pdf.
export function exportInvoiceReport({ period, filterLabel }, stats, rows, fmt) {
  const base = `rapport_couts_${period.from}_${period.to}`;
  const total = stats?.total || 0;
  const catRows = (stats?.by_category || []).map((c) => [catLabel(c.category), money(c.total), total ? `${Math.round((c.total / total) * 100)}%` : "0%"]);
  const detRows = (rows || []).map((r) => [(r.date || "").slice(0, 10), r.vehicle_name || "—", catLabel(r.category), r.supplier || "—", r.invoice_number || "—", money(r.amount)]);

  if (fmt === "csv") {
    const lines = [];
    lines.push("Rapport de couts - Boomerang Pacific Tracking");
    lines.push(`Periode,${period.from} au ${period.to}`);
    if (filterLabel) lines.push(`Filtre,"${filterLabel}"`);
    lines.push("");
    lines.push("Cout par famille");
    lines.push("Categorie,Montant,Part");
    catRows.forEach((r) => lines.push(r.map((v) => `"${v}"`).join(",")));
    lines.push(`"TOTAL","${money(total)}","100%"`);
    lines.push("");
    lines.push("Detail des factures");
    lines.push("Date,Vehicule,Categorie,Fournisseur,N,Montant");
    detRows.forEach((r) => lines.push(r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")));
    dl(new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" }), base + ".csv");
  } else if (fmt === "pdf") {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    doc.setFillColor(23, 162, 184); doc.rect(0, 0, 210, 16, "F");
    doc.setTextColor(255); doc.setFontSize(13);
    doc.text("Boomerang Pacific Tracking - Rapport de couts", 10, 10);
    doc.setTextColor(15, 23, 42); doc.setFontSize(9);
    doc.text(`Periode : ${period.from} au ${period.to}${filterLabel ? "   -   " + filterLabel : ""}`, 10, 23);
    doc.setFontSize(11);
    doc.text(`Total global : ${money(total)}  (${stats?.count || 0} facture(s))`, 10, 30);
    autoTable(doc, {
      head: [["Famille", "Montant", "Part"]], body: catRows, startY: 34,
      styles: { fontSize: 9 }, headStyles: { fillColor: [23, 162, 184] },
      foot: [["TOTAL", money(total), "100%"]], footStyles: { fillColor: [245, 130, 31], textColor: 255 },
    });
    autoTable(doc, {
      head: [["Date", "Vehicule", "Famille", "Fournisseur", "N", "Montant"]], body: detRows,
      startY: (doc.lastAutoTable?.finalY || 34) + 6, styles: { fontSize: 7.5, cellPadding: 1 }, headStyles: { fillColor: [23, 162, 184] },
    });
    doc.save(base + ".pdf");
  }
}
